<script>
import {defineComponent} from 'vue'

// Recursive editor for nested child economic-classification indicators.
// One instance renders the rows for a single layer; if the configured depth
// allows it, each row embeds another instance for its own children.
export default defineComponent({
  name: "EconomicIndicatorChildRows",
  emits: ['update:modelValue'],
  props: {
    // The array of child indicator rows for this layer.
    modelValue: {type: Array, default: () => []},
    // Classification whose children are selectable at this layer.
    parentClassificationId: {type: Number, default: null},
    // Full path of this array inside the root el-form model (for validation props).
    propPrefix: {type: String, required: true},
    // Layer this instance renders (parent indicator = 1, so children start at 2).
    level: {type: Number, default: 2},
    // Deepest layer allowed.
    maxLevel: {type: Number, default: 3},
  },
  data() {
    return {
      initialized: false,
      options: [],
      optionsLoading: false,
      quarters: [{value: 1}, {value: 2}, {value: 3}, {value: 4}],
      rules: {
        economic_classification_id: [{required: true, message: "Majburiy maydon", trigger: "change"}],
        year: [{required: true, message: "Majburiy maydon", trigger: "change"}],
        quarter: [{required: true, message: "Majburiy maydon", trigger: "change"}],
        amount: [{required: true, message: "Majburiy maydon", trigger: "blur"}],
      }
    }
  },
  computed: {
    title() {
      return this.level >= 3 ? "Ichki ko'rsatgichlar" : "Bola ko'rsatgichlar";
    },
    addLabel() {
      return this.level >= 3 ? "Ichki ko'rsatgich qo'shish" : "Bola qo'shish";
    }
  },
  watch: {
    parentClassificationId(val, oldVal) {
      if (val) {
        this.loadOptions(val);
      } else {
        this.options = [];
      }
      // When the parent classification is changed by the user the existing rows
      // belong to the old parent and must be dropped. Skip the very first
      // assignment (handled in created) so hydrated rows survive edit.
      if (this.initialized && val !== oldVal && this.modelValue.length) {
        this.$emit('update:modelValue', []);
      }
    }
  },
  methods: {
    async loadOptions(parentId) {
      this.optionsLoading = true;
      try {
        const {data} = await this.$http.get('api/v1/economic-classifications', {
          params: {parent_id: parentId, limit: 100, offset: 0}
        });
        this.options = data.data.map(c => ({id: c.id, title: c.title}));
      } catch (e) {
        this.options = [];
      } finally {
        this.optionsLoading = false;
      }
    },
    disabledYear(date) {
      return date.getFullYear() < 2018;
    },
    addRow() {
      this.$emit('update:modelValue', [
        ...this.modelValue,
        {
          id: null,
          economic_classification_id: null,
          year: null,
          quarter: null,
          amount: null,
          child_economic_classification_indicators: [],
        },
      ]);
    },
    removeRow(index) {
      this.$emit('update:modelValue', this.modelValue.filter((_, i) => i !== index));
    }
  },
  created() {
    if (this.parentClassificationId) {
      this.loadOptions(this.parentClassificationId);
    }
    this.initialized = true;
  }
})
</script>

<template>
  <div class="child-rows">
    <div class="child-header">
      <span class="child-title">{{ title }}</span>
      <el-button
          size="small"
          type="primary"
          plain
          :disabled="!parentClassificationId"
          @click="addRow"
      >
        + {{ addLabel }}
      </el-button>
    </div>

    <el-alert
        v-if="!parentClassificationId"
        type="info"
        :closable="false"
        title="Qo'shish uchun avval yuqoridagi klassifikatorni tanlang"
    />

    <div v-for="(row, index) in modelValue" :key="index" class="child-card">
      <el-row :gutter="10">
        <el-col :span="24">
          <el-form-item
              label="Klassifikatorni tanlang"
              :prop="`${propPrefix}.${index}.economic_classification_id`"
              :rules="rules.economic_classification_id"
          >
            <el-select
                v-model="row.economic_classification_id"
                :loading="optionsLoading"
                filterable
                clearable
                placeholder="Klassifikatorni tanlang"
                style="width: 100%"
            >
              <el-option v-for="opt in options" :key="opt.id" :label="opt.title" :value="opt.id"/>
            </el-select>
          </el-form-item>
        </el-col>

        <el-col :xs="24" :sm="12" :md="8">
          <el-form-item
              label="Yil"
              :prop="`${propPrefix}.${index}.year`"
              :rules="rules.year"
          >
            <el-date-picker
                v-model="row.year"
                type="year"
                format="YYYY"
                value-format="YYYY"
                :disabled-date="disabledYear"
                style="width: 100%"
                placeholder="Yil"
            />
          </el-form-item>
        </el-col>

        <el-col :xs="24" :sm="12" :md="8">
          <el-form-item
              label="Chorak"
              :prop="`${propPrefix}.${index}.quarter`"
              :rules="rules.quarter"
          >
            <el-select v-model="row.quarter" style="width: 100%" placeholder="Chorak">
              <el-option v-for="q in quarters" :key="q.value" :value="q.value" :label="q.value"/>
            </el-select>
          </el-form-item>
        </el-col>

        <el-col :xs="24" :sm="24" :md="8">
          <el-form-item
              label="Miqdor"
              :prop="`${propPrefix}.${index}.amount`"
              :rules="rules.amount"
          >
            <el-input v-model.number="row.amount" type="number" placeholder="Miqdor"/>
          </el-form-item>
        </el-col>

        <el-col :span="24" v-if="level < maxLevel">
          <economic-indicator-child-rows
              v-model="row.child_economic_classification_indicators"
              :parent-classification-id="row.economic_classification_id"
              :prop-prefix="`${propPrefix}.${index}.child_economic_classification_indicators`"
              :level="level + 1"
              :max-level="maxLevel"
          />
        </el-col>

        <el-col :span="24">
          <el-button size="small" type="danger" plain @click="removeRow(index)">
            O'chirish
          </el-button>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<style scoped>
.child-rows {
  width: 100%;
}

.child-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 4px 0 12px;
  padding-top: 6px;
  border-top: 1px solid #ebeef5;
}

.child-title {
  font-weight: 600;
  color: #303133;
}

.child-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px 12px 0;
  margin-bottom: 12px;
  background: #fafafa;
}

/* Nested layers get a subtle indent + accent so depth is readable. */
.child-card .child-card {
  background: #f3f4f6;
  border-left: 3px solid #c6e2ff;
}
</style>
